import router from '@/core/router';

/**
 * Single-flight guard. When a query keeps failing (a stale session cookie, for
 * instance) the Apollo error link fires once per operation while the router
 * transition to `/login` is still in flight. Without this latch each call
 * queues a fresh `router.replace`, the new navigation re-runs the auth
 * middleware, which re-fires `fetchMe`, which fails, which re-enters this
 * function — an infinite loop.
 */
let loginRedirectInFlight = false;

/**
 * Send the user to the login page, keeping the current URL as `redirect_uri`
 * so they land back where they were.
 */
export const goToLogin = (): void => {
  if (
    loginRedirectInFlight
    || window.location.pathname === '/login'
    || router.currentRoute.value.name === 'Login'
  ) {
    return;
  }

  loginRedirectInFlight = true;

  const currentPath = window.location.pathname + window.location.search;

  void router
    .replace({ name: 'Login', query: { redirect_uri: currentPath } })
    .finally(() => {
      loginRedirectInFlight = false;
    });
};

/**
 * The magic-link round-trip bounces the user out to email and back via
 * `/magic-link`, which loses the `?redirect_uri=…` the original `/login` URL
 * carried. The intended destination is stashed in a short-lived client cookie
 * at email-submit time so `MagicLinkPage` — which never sees the original
 * query — can route the user on after a successful consume.
 *
 * The TTL covers the longest full path: the welcome link's own lifetime plus a
 * buffer for clock skew. Keeping it that long is safe because every fresh
 * `/login` visit either overwrites the value or clears it on submit, and every
 * successful sign-in clears it explicitly — so a stale value cannot bleed into
 * an unrelated later sign-in.
 */
const REDIRECT_COOKIE_NAME = 'dc_auth_redirect_uri';
const REDIRECT_COOKIE_MAX_AGE_S = 7 * 24 * 60 * 60 + 10 * 60;

/**
 * True for a path that can only land back on this origin.
 *
 * Both rejected prefixes are protocol-relative URLs, not paths: `//evil.example`
 * is a URL with an empty scheme, and browsers normalise a backslash to a forward
 * slash in the authority position, so `/\evil.example` lands on evil.example too.
 * Either one handed to `router.replace` — or written into the redirect cookie —
 * is an open redirect that survives a real sign-in, which is exactly the shape
 * a phishing link wants.
 */
const isSameOriginPath = (value: string): boolean =>
  value.startsWith('/') && !value.startsWith('//') && !value.startsWith('/\\');

/**
 * Stash the redirect target for the magic-link round-trip. Pass `null` (or an
 * empty string) to clear — done on a plain `/login` visit so a leftover value
 * from a previous session cannot bleed into a fresh one.
 *
 * The value must be a same-origin relative path, or a tampered cookie becomes
 * an open redirect after auth.
 *
 * Accepts the raw shape Vue Router exposes for `route.query.*` — a repeated
 * `?redirect_uri=…&redirect_uri=…` arrives as an array. Anything that is not a
 * single string is treated as a clear; without that guard the array would
 * crash this function on `.startsWith` and take the submit handler with it.
 */
export const stashAuthRedirect = (
  value: string | (string | null)[] | null | undefined,
): void => {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  const single = typeof value === 'string' ? value : null;

  if (!single || !isSameOriginPath(single)) {
    document.cookie = `${REDIRECT_COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax${secure}`;

    return;
  }

  document.cookie
    = `${REDIRECT_COOKIE_NAME}=${encodeURIComponent(single)}; path=/; max-age=${REDIRECT_COOKIE_MAX_AGE_S}; SameSite=Lax${secure}`;
};

/**
 * Read the stashed redirect target. Returns `null` when the cookie is absent,
 * malformed, or fails the same-origin-relative-path check — the caller falls
 * back to `/`.
 */
export const readAuthRedirect = (): string | null => {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${REDIRECT_COOKIE_NAME}=([^;]+)`));
  const raw = match?.[1];

  if (!raw) {
    return null;
  }

  try {
    const value = decodeURIComponent(raw);

    if (!isSameOriginPath(value)) {
      return null;
    }

    return value;
  } catch {
    return null;
  }
};

/** Clear the stashed redirect target, once it has been consumed. */
export const clearAuthRedirect = (): void => {
  stashAuthRedirect(null);
};

/**
 * Same-origin relative path or `/`. Vue Router exposes a repeated query key as
 * `string[]`, so a user-controlled `?redirect_uri=/a&redirect_uri=//evil.com`
 * would otherwise reach `router.replace` untouched.
 */
export const safeRedirectPath = (value: unknown): string => {
  return typeof value === 'string' && isSameOriginPath(value) ? value : '/';
};
